import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import stringify from '../utils/stableStringify';
import format from 'string-format';

import AntLocalization from '../utils/AntLocalization';
import AntConfig from '../utils/AntConfig';
import AntCondition from '../utils/AntCondition';
import AntArray from '../utils/AntArray';
import AntJSON from '../utils/AntJSON';
import { AntGlobal } from '../utils/AntContext';

import AntSimpleDataSource from '../components/AntSimpleDataSource';
import AntScheduler from '../components/AntScheduler';
import AntCardViewSearchBar from './AntPOSCardViewSearchBar';
import AntPOSCategories from './AntPOSCategories';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal?.appConfig?.Services) {
            const found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table || props.API?.entityName || '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        const cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (!Array.isArray(appGlobal.tableConfigs)) appGlobal.tableConfigs = [];
        appGlobal.tableConfigs.push({ tableName, config: cnf });
        return cnf;
    }, [tableName, appGlobal, props.fields]);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const [shouldRender, setShouldRender] = useState(1);
    const loading = useRef(false);
    const loaded = useRef(false);

    const scheduler = useRef();
    const searchBar = useRef();
    const isForeground = useRef(false);
    isForeground.current = props.isForeground !== false;
    const autoRefreshTimer = useRef();
    const afterActionRefreshTimeout = useRef();

    const [itemsCats1, setItemsCats1] = useState([]);
    const [itemsCats1Loaded, setItemsCats1Loaded] = useState(false);
    const [selectedCats1, setSelectedCats1] = useState([]);

    const selectedCategories = useMemo(() => selectedCats1.flatMap(s => s.value), [selectedCats1]);

    const defaultPageSize = props.defaultPageSize || 100;
    const schedulerViewDataSource = useRef(new AntSimpleDataSource({ paginate: true, pageSize: defaultPageSize }));
    const searchConditions = useRef({ ...props.searchConditions });
    const [finalSearchConditions, setFinalSearchConditions] = useState({});
    const [searchText, setSearchText] = useState('');

    const initGridRowActions = useCallback(() => {
        let actions = [];
        if (props.showActions) {
            const eName = props.rowActionPack || props.table || props.API?.entityName;
            actions.push(...config.getCnfSearchActions(eName));
        }

        if (Array.isArray(props.rowActions)) actions.push(...props.rowActions);

        if (Array.isArray(props.excludesRowActions)) {
            actions = actions.filter(item => !props.excludesRowActions.includes(item.code));
        }

        return actions.filter(item => AntCondition.isVisible(null, item));
    }, [props.showActions, props.rowActionPack, props.table, props.API, props.rowActions, props.excludesRowActions, config]);

    const gridRowActions = useMemo(() => initGridRowActions(), [initGridRowActions]);

    const dsGetSearchText = useCallback(() => searchText, [searchText]);

    const dsGetQueryFields = useCallback(() => {
        const fields = new Set(['created_at', 'checksum', 'id', 'code']);

        const addIfValid = (f) => {
            if (f && typeof f === 'object' && f.key) fields.add(f.key);
        };

        if (props.showDetail) {
            if (Array.isArray(props.showDetail)) props.showDetail.forEach(addIfValid);
            else addIfValid(props.showDetail);
        }

        if (Array.isArray(props.queryFields)) props.queryFields.forEach(f => fields.add(f));
        if (Array.isArray(props.selectFields)) props.selectFields.forEach(f => fields.add(f));

        if (typeof props.getQueryFields === 'function') {
            const extra = props.getQueryFields();
            if (Array.isArray(extra)) extra.forEach(f => fields.add(f));
        }

        const addKeys = (obj) => {
            if (obj) AntJSON.getKeys(obj).forEach(f => fields.add(f));
        };

        addKeys(props.showDetail);
        addKeys(props.scheduler);
        addKeys(props.rowStyles);
        addKeys(props.rowActions);
        addKeys(gridRowActions);

        if (props.scheduler) {
            config.cnfFields.forEach(cnf => {
                if (fields.has(cnf.dataField)) {
                    if (cnf.visibleOnMain) addKeys(cnf.visibleOnMain);
                    if (cnf.visibleSelf) addKeys(cnf.visibleSelf);
                }
            });
        }

        const distinctFields = Array.from(fields).map(f => (typeof f === 'object' && f ? f.field : f));
        const filtered = AntArray.distinct(distinctFields.filter(f => config.hasField(f)));

        return Array.from(fields).filter(f => {
            const key = typeof f === 'object' && f ? f.field : f;
            return filtered.includes(key);
        });
    }, [config, gridRowActions, props.showDetail, props.queryFields, props.selectFields, props.getQueryFields, props.scheduler, props.rowStyles, props.rowActions]);

    const dsGetSearchConditions = useCallback(() => {
        return searchConditions.current ? JSON.parse(stringify(searchConditions.current)) : {};
    }, []);

    const generateSearchConditions = useCallback((opts) => {
        let objS = {};
        if (props.searchCategories1) {
            selectedCats1.forEach(c => {
                if (objS[c.field] !== undefined) objS[c.field].push(c.value);
                else objS[c.field] = [c.value];
            });
        }

        Object.keys(objS).forEach(k => {
            if (Array.isArray(objS[k]) && objS[k].length === 1) objS[k] = objS[k][0];
        });

        let newObj = {};
        if (props.searchConditions) {
            newObj = { ...newObj, ...(typeof props.searchConditions === 'function' ? props.searchConditions() : props.searchConditions) };
        }

        if (Array.isArray(props.searchFields) && props.searchFields.length > 0) {
            Object.keys(newObj).forEach(k => {
                const found = props.searchFields.find(f => typeof f === 'object' && f.alias && f.field === k);
                if (found) {
                    newObj[found.alias] = newObj[k];
                    delete newObj[k];
                }
            });

            const suffixes = ['_oneMonth', '_oneYear', '_startDate', '_startMonth', '_startYear', '_endDate', '_endMonth', '_endYear'];
            Object.keys(newObj).forEach(k => {
                const found = props.searchFields.find(f => {
                    if (f === k || (typeof f === 'object' && f.alias === k)) return true;
                    if (typeof f === 'string') {
                        return suffixes.some(s => f + s === k || k + s === f);
                    }
                    return false;
                });
                if (!found) newObj[k] = undefined;
            });
        }

        newObj = { ...newObj, ...objS };

        Object.keys(newObj).forEach(k => {
            if (newObj[k] != null && typeof newObj[k] === 'object') newObj[k] = AntCondition.getValueFromSession(newObj[k]);
            if (newObj[k] === null || newObj[k] === '') newObj[k] = undefined;
        });

        Object.keys(newObj).forEach(key => {
            if (newObj[key] === undefined) delete newObj[key];
            else if (Array.isArray(newObj[key])) newObj[key + '_search_inner_join'] = true;
        });

        if (props.isSearching) newObj.isSearching = true;

        if (!opts?.byPassCheckActions && Array.isArray(gridRowActions) && gridRowActions.length > 0) {
            newObj.checkActions = gridRowActions.map(a => a.code);
        }

        searchConditions.current = newObj;
        if (stringify(newObj) !== stringify(finalSearchConditions)) setFinalSearchConditions(newObj);
        return newObj;
    }, [props.searchCategories1, selectedCats1, props.searchConditions, props.searchFields, props.isSearching, gridRowActions, finalSearchConditions]);

    const refresh = useCallback(() => {
        generateSearchConditions();
    }, [generateSearchConditions]);

    const forceReload = useCallback((options = {}) => {
        if (
            options.allowBackground !== true &&
            (!isForeground.current || (typeof document !== 'undefined' && document.visibilityState === 'hidden'))
        )
            return false;
        generateSearchConditions({ autoReload: true });
        if (props.scheduler && scheduler.current) scheduler.current.reload();
    }, [generateSearchConditions, props.scheduler]);

    const onRowAction = useCallback(async (e, done) => {
        const actionMapping = {
            actionCancel: 'cancel',
            actionDelete: 'delete',
            actionRelease: 'release',
            actionClose: 'close',
            actionApprove: 'approve',
            actionPost: 'post',
            actionReject: 'reject'
        };
        const actionName = actionMapping[e.actionId] || e.actionId;

        if (props.onRowAction) {
            const close = await props.onRowAction({
                actionName,
                table: tableName,
                mainCode: e.data?.code,
                mainID: e.data?.id,
                mainData: e.data
            });
            if (close) return done?.();
        }

        const btn = Array.isArray(gridRowActions) ? gridRowActions.find(a => a.code === e.actionId) : null;
        if (!btn) return done?.();

        const actAPI = btn.API || props.API;
        let result;

        if (props.rowActionCustomData) {
            result = await actAPI.postActionWithData(actionName, props.rowActionCustomData(e));
        } else {
            result = await actAPI.postAction(actionName, e.data.code, e.data.id);
        }

        if (btn && !btn.opts?.byPassRefreshGrid && props.refreshAfterAction !== false) {
            await forceReload();
            if (afterActionRefreshTimeout.current) clearTimeout(afterActionRefreshTimeout.current);
            afterActionRefreshTimeout.current = setTimeout(forceReload, 1500);
        }

        props.afterRowAction?.({
            actionName,
            table: tableName,
            mainCode: e.data.code,
            mainID: e.data.id,
            mainData: e.data,
            actionResult: result
        });

        done?.();
    }, [props.onRowAction, tableName, gridRowActions, props.API, props.rowActionCustomData, props.refreshAfterAction, forceReload, props.afterRowAction]);

    const initCardViewDataSourceConfig = useCallback(() => ({
        key: props.keyExpr,
        API: props.API,
        url: props.url,
        loadFunc: props.loadFunc,
        remotePaging: false,
        getSearchConditions: dsGetSearchConditions,
        getQueryFields: dsGetQueryFields,
        getSearchText: dsGetSearchText,
        onGotData: (data) => scheduler.current?.onLoadData(data)
    }), [props.keyExpr, props.API, props.url, props.loadFunc, dsGetSearchConditions, dsGetQueryFields, dsGetSearchText]);

    const onItemClick = useCallback(async (data) => {
        if (props.onCellClick) {
            const close = await props.onCellClick({
                table: tableName,
                mainCode: data?.code,
                mainID: data?.id,
                mainData: data
            });
            if (close) return;
        }
    }, [props.onCellClick, tableName]);

    useImperativeHandle(ref, () => ({
        forceReload, refresh, forceRender,
        isDataLoaded: () => Boolean(loaded.current),
        isDataLoading: () => Boolean(loading.current)
    }));

    useEffect(() => {
        schedulerViewDataSource.current.reInit(initCardViewDataSourceConfig());
        forceReload();
    }, [initCardViewDataSourceConfig, forceReload]);

    useEffect(() => {
        generateSearchConditions();
    }, [generateSearchConditions]);

    const onSearchText = useCallback((text) => {
        setSearchText(text);
        props.onSearchText?.(text);
    }, [props.onSearchText]);

    const onRefreshClick = useCallback(() => {
        forceReload();
        props.onRefreshClick?.();
    }, [forceReload, props.onRefreshClick]);

    useEffect(() => {
        if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
        if (props.autoRefreshSeconds) {
            autoRefreshTimer.current = setInterval(() => {
                if (isForeground.current) forceReload();
            }, props.autoRefreshSeconds * 1000);
        }
        return () => clearInterval(autoRefreshTimer.current);
    }, [props.autoRefreshSeconds, forceReload]);

    const isCategoriesShouldVisible = useCallback((cats) => {
        if (!cats) return false;
        if (!AntCondition.isVisible({}, cats)) return false;
        if (cats.visible === true) return true;

        if (searchConditions.current && cats.lookups) {
            if (cats.lookups.some(l => Object.keys(searchConditions.current).includes(l.field))) return true;
        }

        if (!itemsCats1Loaded) return true;

        if (cats.visibleDependOthers) {
            const others = Array.isArray(cats.visibleDependOthers) ? cats.visibleDependOthers : [];
            const isOr = cats.visibleDependOthersType === 'OR';
            if (isOr) return selectedCategories.length > 0;
            return others.length > 0 ? selectedCategories.length > 0 : true;
        }

        return itemsCats1.length > 1 || selectedCats1.length > 0;
    }, [itemsCats1Loaded, itemsCats1, selectedCats1, selectedCategories]);

    const cats1Visible = isCategoriesShouldVisible(props.searchCategories1);

    const onLoadedDataCat1 = useCallback((e) => {
        setItemsCats1(e.allItems || []);
        setItemsCats1Loaded(true);
    }, []);

    const onSelectionChangedCat1 = useCallback((e) => {
        setItemsCats1(e.allItems);
        setSelectedCats1(e.selectedItems);
        forceReload();
    }, [forceReload]);

    const allowQueryCat1 = useCallback(() => {
        if (props.searchCategories1?.visibleDependOthers) return selectedCategories.length > 0;
        return true;
    }, [props.searchCategories1, selectedCategories]);

    return (
        <React.Fragment>
            {shouldRender && props.scheduler && props.showSearchBar !== false && (
                <AntCardViewSearchBar
                    ref={searchBar}
                    API={props.API}
                    showSearchBox={props.showSearchBox}
                    onSearchText={onSearchText}
                    onRefreshClick={onRefreshClick}
                />
            )}

            {shouldRender && props.scheduler && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: props.showSearchBar !== false ? '2px' : '10px' }}>
                    {props.searchCategories1 && (
                        <AntPOSCategories
                            API={props.API}
                            searchText={String(searchText)}
                            searchConditions={searchConditions}
                            searchCategories={props.searchCategories1}
                            selectedItems={selectedCats1}
                            onLoadedData={onLoadedDataCat1}
                            onSelectionChanged={onSelectionChangedCat1}
                            visible={cats1Visible}
                            allowQuery={allowQueryCat1}
                        />
                    )}
                </div>
            )}

            {shouldRender && props.scheduler && (
                <AntScheduler
                    ref={scheduler}
                    API={props.API}
                    {...props.scheduler}
                    dataSource={schedulerViewDataSource.current.instance()}
                    rawDataSource={schedulerViewDataSource.current}
                    searchConditions={{ ...props.scheduler?.searchConditions }}
                    onAppointmentClick={onItemClick}
                />
            )}
        </React.Fragment>
    );
});
